<script module lang="ts">
  export interface CategoryRow {
    label: string
    satisfied: number
    total: number
  }
</script>

<script lang="ts">
  import {
    Chart,
    BarController,
    BarElement,
    LinearScale,
    CategoryScale,
    Tooltip,
    type ChartConfiguration,
    type Plugin,
  } from 'chart.js'
  import { chartTheme, tooltipStyle } from '../../composables/chartTheme'
  import { tStore } from '../../i18n'

  Chart.register(BarController, BarElement, LinearScale, CategoryScale, Tooltip)

  interface Props {
    rows: CategoryRow[]
  }

  let { rows }: Props = $props()

  let canvas = $state<HTMLCanvasElement | undefined>()

  // Paints "satisfied/total" just past the end of each row's full bar, so the
  // absolute counts stay readable even when a category's bar is short.
  const countLabelsPlugin: Plugin<'bar'> = {
    id: 'categoryCountLabels',
    afterDatasetsDraw(chart) {
      const { ctx } = chart
      const meta0 = chart.getDatasetMeta(0)
      const meta1 = chart.getDatasetMeta(1)
      ctx.save()
      ctx.font = '600 11px Inter, system-ui, sans-serif'
      ctx.fillStyle = chartTheme.textMuted
      ctx.textBaseline = 'middle'
      ctx.textAlign = 'left'
      rows.forEach((row, i) => {
        // The unmet segment (dataset 1) sits on top of satisfied, so its bar's
        // right edge is the end of the full stack. Fall back to satisfied when
        // there is no unmet segment element.
        const tip = (meta1.data[i] ?? meta0.data[i]) as unknown as
          | { x: number; y: number }
          | undefined
        if (!tip) return
        ctx.fillText(`${row.satisfied}/${row.total}`, tip.x + 6, tip.y)
      })
      ctx.restore()
    },
  }

  function buildConfig(): ChartConfiguration<'bar'> {
    const satLabel = $tStore('views.certificateAnalysis.legend.satisfied')
    const unmetLabel = $tStore('views.certificateAnalysis.legend.notSatisfied')
    return {
      type: 'bar',
      data: {
        labels: rows.map((r) => r.label),
        datasets: [
          {
            label: satLabel,
            data: rows.map((r) => r.satisfied),
            backgroundColor: chartTheme.satisfied,
            borderRadius: 4,
            borderSkipped: false,
          },
          {
            label: unmetLabel,
            data: rows.map((r) => Math.max(0, r.total - r.satisfied)),
            backgroundColor: chartTheme.unmet,
            borderRadius: 4,
            borderSkipped: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        // Room on the right for the count labels drawn past the bar end.
        layout: { padding: { right: 48, left: 4, top: 4, bottom: 4 } },
        scales: {
          x: {
            stacked: true,
            beginAtZero: true,
            ticks: { color: chartTheme.textMuted, precision: 0, font: { size: 11 } },
            grid: { color: chartTheme.grid },
          },
          y: {
            stacked: true,
            ticks: { color: chartTheme.text, font: { size: 12 } },
            grid: { display: false },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            ...tooltipStyle,
            callbacks: {
              label: (ctx) => {
                const row = rows[ctx.dataIndex]
                const v = ctx.parsed.x as number
                const pct = row && row.total > 0 ? Math.round((v / row.total) * 100) : 0
                return ` ${ctx.dataset.label}: ${v} · ${pct}%`
              },
            },
          },
        },
      },
      plugins: [countLabelsPlugin],
    }
  }

  $effect(() => {
    if (!canvas) return
    void rows
    const chart = new Chart(canvas, buildConfig())
    return () => chart.destroy()
  })
</script>

<div class="bar-wrap">
  <canvas bind:this={canvas}></canvas>
</div>

<style>
  .bar-wrap {
    position: relative;
    width: 100%;
    height: 160px;
  }
</style>
